import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RootState, AppDispatch} from 'store/store';
import {
  setDocumentTemplateProgram,
  setIsEditing,
} from './documentTemplateProgramSlice';
import {DocumentTemplateProgramThunks} from './documentTemplateProgramThunks';
import {DocumentTemplate} from 'types/documentTemplate';
import {DocumentTemplateService} from 'services/documentTemplateService';
import {useParams} from 'react-router-dom';
import TextInput from 'components/inputs/TextInput';
import CustomSelect from 'components/inputs/CustomSelect';
import RadioInput from 'components/inputs/RadioInput';
import SubmitButton from 'components/SubmitButton';
import XIcon from 'assets/images/icons/XIcon';

interface DocumentTemplateProgramDrawerProps {
  closeDrawer: () => void;
}

const DocumentTemplateProgramDrawer: React.FC<
  DocumentTemplateProgramDrawerProps
> = ({closeDrawer}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {programId} = useParams<{programId: string}>();
  const selectedItem = useSelector(
    (state: RootState) => state.documentTemplateProgram.documentTemplateProgram,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [documentTemplateOptions, setDocumentTemplateOptions] = useState<
    {label: string; value: string}[]
  >([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);

  const [updatedDocumentTemplateProgram, setUpdatedDocumentTemplateProgram] =
    useState({
      documentTemplateId: '',
      programId: undefined,
      tabName: '',
      description: '',
      approvable: false,
      faxable: false,
      alerts: false,
      showDuringIntake: false,
      showDuringAssessment: false,
      showWhenActive: false,
      allowInPortal: false,
      blockCaseNotesWhenDue: false,
      showClientNameOnFooter: false,
      showClientExternalIdOnFooter: false,
      showReportDateOnFooter: false,
      alertDaysAfterFinalized: 0,
      alertDaysBeforeDue: 0,
      pertainsTo: 'all', // Nuevo campo para manejar el scope
    });

  // Eliminar isShared y usar pertainsTo en su lugar
  const pertainsToOptions = [
    {value: 'all', label: 'All programs for this client'},
    {value: 'program', label: 'Only this program'},
  ];

  useEffect(() => {
    const isFormValid =
      updatedDocumentTemplateProgram.tabName.trim() !== '' &&
      updatedDocumentTemplateProgram.documentTemplateId;
    setIsSaveDisabled(!isFormValid);
  }, [
    updatedDocumentTemplateProgram.tabName,
    updatedDocumentTemplateProgram.documentTemplateId,
  ]);

  useEffect(() => {
    const fetchDocumentTemplates = async () => {
      setIsLoadingTemplates(true);
      try {
        const response = await DocumentTemplateService.index(
          1,
          {where: {}},
          '',
          '',
          100,
        );
        const options =
          response.data?.map((template: DocumentTemplate) => ({
            label: template.name,
            value: template.id,
          })) || [];
        setDocumentTemplateOptions(options);
      } catch (error) {
        console.error('Error fetching document templates:', error);
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    fetchDocumentTemplates();
  }, []);

  useEffect(() => {
    if (selectedItem) {
      setUpdatedDocumentTemplateProgram({
        documentTemplateId: selectedItem.documentTemplateId,
        programId: selectedItem.programId,
        tabName: selectedItem.tabName,
        description: selectedItem.description || '',
        approvable: selectedItem.approvable,
        faxable: selectedItem.faxable,
        alerts: selectedItem.alerts,
        showDuringIntake: selectedItem.showDuringIntake,
        showDuringAssessment: selectedItem.showDuringAssessment,
        showWhenActive: selectedItem.showWhenActive,
        allowInPortal: selectedItem.allowInPortal,
        blockCaseNotesWhenDue: selectedItem.blockCaseNotesWhenDue,
        showClientNameOnFooter: selectedItem.showClientNameOnFooter,
        showClientExternalIdOnFooter: selectedItem.showClientExternalIdOnFooter,
        showReportDateOnFooter: selectedItem.showReportDateOnFooter,
        alertDaysAfterFinalized: selectedItem.alertDaysAfterFinalized,
        alertDaysBeforeDue: selectedItem.alertDaysBeforeDue,
        pertainsTo: selectedItem.programId ? 'program' : 'all',
      });
    } else {
      setUpdatedDocumentTemplateProgram({
        documentTemplateId: '',
        programId: undefined,
        tabName: '',
        description: '',
        approvable: false,
        faxable: false,
        alerts: false,
        showDuringIntake: false,
        showDuringAssessment: false,
        showWhenActive: false,
        allowInPortal: false,
        blockCaseNotesWhenDue: false,
        showClientNameOnFooter: false,
        showClientExternalIdOnFooter: false,
        showReportDateOnFooter: false,
        alertDaysAfterFinalized: 0,
        alertDaysBeforeDue: 0,
        pertainsTo: 'all',
      });
    }
  }, [selectedItem]);

  const handleChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | {target: {id: string; value: any}},
  ) => {
    const {id, value} = e.target;
    setUpdatedDocumentTemplateProgram({
      ...updatedDocumentTemplateProgram,
      [id]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const finalProgramId =
        updatedDocumentTemplateProgram.pertainsTo === 'program' && programId
          ? parseInt(programId)
          : null;

      const backendData = {
        document_template_id: updatedDocumentTemplateProgram.documentTemplateId,
        program_id: finalProgramId,
        tab_name: updatedDocumentTemplateProgram.tabName,
        description: updatedDocumentTemplateProgram.description,
        approvable: updatedDocumentTemplateProgram.approvable,
        faxable: updatedDocumentTemplateProgram.faxable,
        alerts: updatedDocumentTemplateProgram.alerts,
        show_during_intake: updatedDocumentTemplateProgram.showDuringIntake,
        show_during_assessment:
          updatedDocumentTemplateProgram.showDuringAssessment,
        show_when_active: updatedDocumentTemplateProgram.showWhenActive,
        allow_in_portal: updatedDocumentTemplateProgram.allowInPortal,
        block_case_notes_when_due:
          updatedDocumentTemplateProgram.blockCaseNotesWhenDue,
        show_client_name_on_footer:
          updatedDocumentTemplateProgram.showClientNameOnFooter,
        show_client_external_id_on_footer:
          updatedDocumentTemplateProgram.showClientExternalIdOnFooter,
        show_report_date_on_footer:
          updatedDocumentTemplateProgram.showReportDateOnFooter,
        alert_days_after_finalized:
          updatedDocumentTemplateProgram.alertDaysAfterFinalized,
        alert_days_before_due:
          updatedDocumentTemplateProgram.alertDaysBeforeDue,
      };

      if (selectedItem) {
        const updateData = {
          ...backendData,
          id: selectedItem.id,
        };
        await dispatch(DocumentTemplateProgramThunks.update(updateData) as any);
      } else {
        await dispatch(
          DocumentTemplateProgramThunks.create(backendData) as any,
        );
      }

      closeDrawer();
      dispatch(setDocumentTemplateProgram(null));
      dispatch(setIsEditing(false));
    } catch (error) {
      console.error('Error saving document template program:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    dispatch(setDocumentTemplateProgram(null));
    dispatch(setIsEditing(false));
    closeDrawer();
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="flex-1 flex flex-col">
        <div className="h-auto p-6 bg-sky-50 border-b border-sky-200 flex justify-between items-start gap-2 w-full">
          <div className="flex flex-col justify-start items-start gap-1">
            <div className="text-lg font-semibold tracking-normal text-cyan-800">
              {selectedItem
                ? 'Edit Report Template'
                : 'Create New Report Template'}
            </div>
            <div className="text-sm font-light tracking-normal leading-5 text-zinc-400">
              {selectedItem ? 'Edit ' : 'Create a new '}report template and set
              its parameters
            </div>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700 focus:outline-none pt-2">
            <XIcon />
          </button>
        </div>
        <div className="flex-1 px-3 pb-6 pt-3 overflow-y-auto">
          <div className="h-full flex flex-col overflow-y-auto overflow-hidden">
            <div className="h-full px-4">
              <form onSubmit={handleSubmit}>
                <div className="flex flex-col space-y-6 items-center mt-4">
                  <div className="flex flex-col gap-[0.625rem] w-full">
                    <label className="w-full block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                      Document Template *
                    </label>
                    {isLoadingTemplates ? (
                      <div className="text-gray-500">Loading templates...</div>
                    ) : (
                      <CustomSelect
                        options={documentTemplateOptions}
                        value={
                          updatedDocumentTemplateProgram.documentTemplateId ||
                          ''
                        }
                        onChange={selectedOption => {
                          handleChange({
                            target: {
                              id: 'documentTemplateId',
                              value: selectedOption
                                ? parseInt(selectedOption.value)
                                : 0,
                            },
                          });
                        }}
                      />
                    )}
                  </div>
                  <div className="flex flex-col gap-[0.625rem] w-full">
                    <TextInput
                      id="tabName"
                      type="text"
                      label="Tab Name *"
                      value={updatedDocumentTemplateProgram.tabName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-[0.625rem] w-full">
                    <TextInput
                      id="description"
                      type="text"
                      label="Description"
                      value={updatedDocumentTemplateProgram.description || ''}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col gap-[0.625rem] w-full">
                    <label className="w-full block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                      Scope Configuration
                    </label>
                    <CustomSelect
                      options={pertainsToOptions}
                      value={updatedDocumentTemplateProgram.pertainsTo}
                      onChange={selectedOption => {
                        handleChange({
                          target: {
                            id: 'pertainsTo',
                            value: selectedOption
                              ? selectedOption.value
                              : 'all',
                          },
                        });
                      }}
                    />
                    {updatedDocumentTemplateProgram.pertainsTo === 'program' &&
                      programId && (
                        <div className="text-sm text-slate-600 dark:text-neutral-400">
                          This configuration will apply only to the current
                          program.
                        </div>
                      )}
                  </div>
                  <div className="flex flex-col gap-[0.625rem] w-full ">
                    <label className="w-full block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                      Report Visibility
                    </label>
                    <div className="flex flex-col gap-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={
                            updatedDocumentTemplateProgram.showDuringIntake
                          }
                          onChange={e =>
                            handleChange({
                              target: {
                                id: 'showDuringIntake',
                                value: e.target.checked,
                              },
                            })
                          }
                          className="mr-2"
                        />
                        <span className="text-sm text-slate-700 dark:text-neutral-300">
                          During Intake
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={
                            updatedDocumentTemplateProgram.showDuringAssessment
                          }
                          onChange={e =>
                            handleChange({
                              target: {
                                id: 'showDuringAssessment',
                                value: e.target.checked,
                              },
                            })
                          }
                          className="mr-2"
                        />
                        <span className="text-sm text-slate-700 dark:text-neutral-300">
                          During Assessment
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={
                            updatedDocumentTemplateProgram.showWhenActive
                          }
                          onChange={e =>
                            handleChange({
                              target: {
                                id: 'showWhenActive',
                                value: e.target.checked,
                              },
                            })
                          }
                          className="mr-2"
                        />
                        <span className="text-sm text-slate-700 dark:text-neutral-300">
                          When Active
                        </span>
                      </label>
                    </div>
                  </div>
                  <div className="flex flex-col gap-[0.625rem] w-full  dark:border-neutral-700">
                    <div className="grid  gap-6">
                      <RadioInput
                        label="Approval Configuration"
                        options={[
                          {id: 'approvable-yes', label: 'Yes', value: true},
                          {id: 'approvable-no', label: 'No', value: false},
                        ]}
                        selectedValue={
                          updatedDocumentTemplateProgram.approvable
                        }
                        onChange={value =>
                          handleChange({
                            target: {id: 'approvable', value},
                          })
                        }
                      />
                      <RadioInput
                        label="Enable E-Faxing"
                        options={[
                          {id: 'faxable-yes', label: 'Yes', value: true},
                          {id: 'faxable-no', label: 'No', value: false},
                        ]}
                        selectedValue={updatedDocumentTemplateProgram.faxable}
                        onChange={value =>
                          handleChange({
                            target: {id: 'faxable', value},
                          })
                        }
                      />
                      <RadioInput
                        label="Allow In Portal"
                        options={[
                          {id: 'allowInPortal-yes', label: 'Yes', value: true},
                          {id: 'allowInPortal-no', label: 'No', value: false},
                        ]}
                        selectedValue={
                          updatedDocumentTemplateProgram.allowInPortal
                        }
                        onChange={value =>
                          handleChange({
                            target: {id: 'allowInPortal', value},
                          })
                        }
                      />
                    </div>
                  </div>

                  {/* Footer Options - Comentado por ahora */}
                  {/*
                  <div className="flex flex-col gap-[0.625rem] w-full">
                    <label className="w-full block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                      Footer Options
                    </label>
                    <div className="flex flex-col gap-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={
                            updatedDocumentTemplateProgram.showClientNameOnFooter
                          }
                          onChange={e =>
                            handleChange({
                              target: {
                                id: 'showClientNameOnFooter',
                                value: e.target.checked,
                              },
                            })
                          }
                          className="mr-2"
                        />
                        <span className="text-sm text-slate-700 dark:text-neutral-300">
                          Client Name
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={
                            updatedDocumentTemplateProgram.showClientExternalIdOnFooter
                          }
                          onChange={e =>
                            handleChange({
                              target: {
                                id: 'showClientExternalIdOnFooter',
                                value: e.target.checked,
                              },
                            })
                          }
                          className="mr-2"
                        />
                        <span className="text-sm text-slate-700 dark:text-neutral-300">
                          Client External Id
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={
                            updatedDocumentTemplateProgram.showReportDateOnFooter
                          }
                          onChange={e =>
                            handleChange({
                              target: {
                                id: 'showReportDateOnFooter',
                                value: e.target.checked,
                              },
                            })
                          }
                          className="mr-2"
                        />
                        <span className="text-sm text-slate-700 dark:text-neutral-300">
                          Report Date
                        </span>
                      </label>
                    </div>
                  </div>
                  */}

                  <div className="flex flex-col gap-[0.625rem] w-full  pb-4 dark:border-neutral-700">
                    <RadioInput
                      label="Enable Alerts"
                      options={[
                        {id: 'alerts-yes', label: 'Yes', value: true},
                        {id: 'alerts-no', label: 'No', value: false},
                      ]}
                      selectedValue={updatedDocumentTemplateProgram.alerts}
                      onChange={value =>
                        handleChange({
                          target: {id: 'alerts', value},
                        })
                      }
                    />
                    {updatedDocumentTemplateProgram.alerts && (
                      <div className="flex flex-col gap-[0.625rem] w-full mt-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                          <div className="flex flex-col gap-[0.625rem]">
                            <TextInput
                              id="alertDaysAfterFinalized"
                              type="number"
                              label="Days after finalized for new report due"
                              min="0"
                              value={updatedDocumentTemplateProgram.alertDaysAfterFinalized.toString()}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="flex flex-col gap-[0.625rem]">
                            <TextInput
                              id="alertDaysBeforeDue"
                              type="number"
                              label="Days before due date to show alert"
                              min="0"
                              value={updatedDocumentTemplateProgram.alertDaysBeforeDue.toString()}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                        <RadioInput
                          label="Block case notes when due"
                          options={[
                            {
                              id: 'blockCaseNotesWhenDue-block',
                              label: 'Block',
                              value: true,
                            },
                            {
                              id: 'blockCaseNotesWhenDue-allow',
                              label: 'Allow',
                              value: false,
                            },
                          ]}
                          selectedValue={
                            updatedDocumentTemplateProgram.blockCaseNotesWhenDue
                          }
                          onChange={value =>
                            handleChange({
                              target: {id: 'blockCaseNotesWhenDue', value},
                            })
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <footer className="flex-shrink-0 px-6 pb-4 pt-5 flex justify-end gap-5">
        <div className="border-t border-slate-200 w-full">
          <div className="flex w-full justify-center items-center gap-x-2 pb-4">
            <SubmitButton
              onClick={handleSubmit}
              label={
                selectedItem ? 'Save Report Template' : 'Create Report Template'
              }
              disabled={isSaveDisabled || isSubmitting}
            />
          </div>
          <div className="flex w-full justify-center items-center gap-x-2 pb-4">
            <button
              type="submit"
              onClick={handleCancel}
              className="self-center text-sm font-light tracking-normal leading-5 text-center text-zinc-400 hover:text-cyan-400">
              Never mind
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DocumentTemplateProgramDrawer;
